import {Component, EventEmitter, Injectable, Output} from '@angular/core';
import {FlatTreeControl} from '@angular/cdk/tree';
import {MatTreeFlatDataSource, MatTreeFlattener} from '@angular/material/tree';
import {SelectionModel} from '@angular/cdk/collections';
import {BehaviorSubject, Observable, of as ofObservable} from 'rxjs';
import {User} from '../../models/user';
import {GroupService} from '../../../core/services/group/group.service';
import {Group} from '../../models/group';

/**
 * Node for group item
 */
export class GroupNode {
  children: GroupNode[];
  item: string;
  id: number;
  users: User[];
}

/** Flat node with expandable and level information */
export class GroupFlatNode {
  item: string;
  level: number;
  expandable: boolean;
  id: number;
  users: User[];
}

/**
 * Checklist database, it can build a tree structured Json object.
 * Each node in Json object represents a group.
 */
@Injectable()
export class ChecklistDatabase {
  dataChange: BehaviorSubject<GroupNode[]> = new BehaviorSubject<GroupNode[]>([]);

  isLoadingResults: boolean = true;

  get data(): GroupNode[] { return this.dataChange.value; }

  groups: Group[];

  constructor(
    public groupService: GroupService
  ) {
    this.initialize();
  }

  initialize() {

    this.groupService.observeAuthorizedGroups().subscribe((response) => {
      this.groups = response;
      const data = this.buildFileTree(this.groups, 0);
      this.isLoadingResults = false;
      // Notify the change.
      this.dataChange.next(data);
    });


  }

  /**
   * Build the file structure tree.
   * The return value is the list of `GroupNode`.
   */
  buildFileTree(groups: Group[], level: number) {
    const data: any[] = [];
    for (const group of groups) {

      const node = new GroupNode();
      node.item = group.name;
      node.users = group.users;
      node.id = group.id;

      if (group.children !== null) {
        node.children = this.buildFileTree(group.children, level + 1);
      }

      data.push(node);
    }
    return data;
  }

}


@Component({
  selector: 'weboffice-common-group-tree',
  templateUrl: './group-tree.component.html',
  styleUrls: ['./group-tree.component.scss'],
  providers: [ChecklistDatabase]
})
export class GroupTreeComponent {

  @Output() groupSelection: EventEmitter<any>;


  /** Map from flat node to nested node. This helps us finding the nested node to be modified */
  flatNodeMap: Map<GroupFlatNode, GroupNode> = new Map<GroupFlatNode, GroupNode>();

  /** Map from nested node to flattened node. This helps us to keep the same object for selection */
  nestedNodeMap: Map<GroupNode, GroupFlatNode> = new Map<GroupNode, GroupFlatNode>();

  treeControl: FlatTreeControl<GroupFlatNode>;

  treeFlattener: MatTreeFlattener<GroupNode, GroupFlatNode>;

  dataSource: MatTreeFlatDataSource<GroupNode, GroupFlatNode>;

  isLoadingResults: boolean;

  /** The selection for checklist */
  checklistSelection = new SelectionModel<GroupFlatNode>(true /* multiple */);


  constructor(private database: ChecklistDatabase) {

    this.groupSelection = new EventEmitter<any>();

    this.treeFlattener = new MatTreeFlattener(this.transformer, this.getLevel,
      this.isExpandable, this.getChildren);
    this.treeControl = new FlatTreeControl<GroupFlatNode>(this.getLevel, this.isExpandable);
    this.dataSource = new MatTreeFlatDataSource(this.treeControl, this.treeFlattener);

    database.dataChange.subscribe(data => {
      this.dataSource.data = data;
      this.isLoadingResults = database.isLoadingResults;
      this.treeControl.expand(this.treeControl.dataNodes[0]);
    });
  }

  getLevel = (node: GroupFlatNode) => node.level;

  isExpandable = (node: GroupFlatNode) => node.expandable;

  getChildren = (node: GroupNode): Observable<GroupNode[]> => {
    return ofObservable(node.children);
  }

  hasChild = (_: number, _nodeData: GroupFlatNode) => _nodeData.expandable;

  hasNoContent = (_: number, _nodeData: GroupFlatNode) => _nodeData.item === '';

  /**
   * Transformer to convert nested node to flat node. Record the nodes in maps for later use.
   */
  transformer = (node: GroupNode, level: number) => {
    const flatNode = this.nestedNodeMap.has(node) && this.nestedNodeMap.get(node)!.item === node.item
      ? this.nestedNodeMap.get(node)!
      : new GroupFlatNode();
    flatNode.item = node.item;
    flatNode.level = level;
    flatNode.expandable = node.children !== undefined ? node.children.length > 0 : false;
    this.flatNodeMap.set(flatNode, node);
    this.nestedNodeMap.set(node, flatNode);
    flatNode.id = node.id;
    flatNode.users = node.users;
    return flatNode;
  }

  /** Whether all the descendants of the node are selected */
  descendantsAllSelected(node: GroupFlatNode): boolean {
    const descendants = this.treeControl.getDescendants(node);
    return descendants.every(child => this.checklistSelection.isSelected(child));
  }

  /** Whether part of the descendants are selected */
  descendantsPartiallySelected(node: GroupFlatNode): boolean {
    const descendants = this.treeControl.getDescendants(node);
    const result = descendants.some(child => this.checklistSelection.isSelected(child));
    return result && !this.descendantsAllSelected(node);
  }
  /** Toggle the node selection. Select/deselect all the descendants node */
  checkSelectionToggle(node: GroupFlatNode): void {
    this.checklistSelection.toggle(node);

    this.afterSelection();
  }

  /** Toggle the node selection. Select/deselect all the descendants node */
  checkParentSelectionToggle(node: GroupFlatNode): void {
    this.checklistSelection.toggle(node);

    const descendants = this.treeControl.getDescendants(node);
    this.checklistSelection.isSelected(node)
      ? this.checklistSelection.select(...descendants)
      : this.checklistSelection.deselect(...descendants);

      this.afterSelection();
  }

  afterSelection() {

    this.groupSelection.emit({selection: this.checklistSelection.selected});
  }

}

